import { Meta } from "components/atoms/Meta";
import { LatestCaseStudy, getPageTranslations } from "utils/server";
import { YuLifeLocale } from "@hooks";
import { Category } from "../../resource-hub";
import { strapiService } from "@services";
import Bugsnag from "@bugsnag/js";
import dynamic from "next/dynamic";
import { ContentType, isLocaleInStaticRoute, PostAttributes, resourceErrorHandler } from "@utils";
import { Logger } from "services/logger";
import { NUMBER_OF_POSTS_TO_FETCH } from "utils/_constants";
import { getStrapiLocale } from "utils/locales/strapi";
import { StaticRoutes } from "@routes";

const ContentHomePageComponent = dynamic(
  () => import("../../../components/pages/Resources/ContentHomePage"),
);

export default function BlogCategoryHomePage({
  locale,
  latestCaseStudy,
  categories,
  slug,
  slugName,
  initialPosts,
  description,
}: {
  locale: YuLifeLocale;
  latestCaseStudy: LatestCaseStudy;
  categories: Category[];
  description: string;
  slug: string;
  slugName: string;
  initialPosts: PostAttributes[];
}) {
  return (
    <>
      <Meta
        title={`${ContentType.blog} - ${slugName} | YuLife`}
        url={`https://yulife.com/blog/category/${slug}/`}
        description={description}
        noIndex
        locale={locale}
      />
      <ContentHomePageComponent
        locale={locale}
        contentType={ContentType.blog}
        slug={slug}
        latestCaseStudy={latestCaseStudy}
        categories={categories}
        initialPosts={initialPosts}
      />
    </>
  );
}

export const getStaticPaths = async () => {
  try {
    const categories = await strapiService.getBlogTags("all");

    return {
      paths: strapiService.formatResourceCategoriesForStaticPaths(categories),
      fallback: false,
    };
  } catch (e) {
    Bugsnag.notify(e);
    Logger.error(e);
    return { paths: [], fallback: false };
  }
};

export const getStaticProps = async ({ locale, params: { category } }) => {
  if (!isLocaleInStaticRoute(locale, StaticRoutes.blog)) {
    return {
      notFound: true,
    };
  }

  const strapiLocale = getStrapiLocale(locale);
  const categories = await strapiService.getBlogTags(strapiLocale);
  const currentCategory = categories.find((c) => c.slug === category);
  const blogPosts = await strapiService.getAllPosts(
    "blog",
    strapiLocale,
    { start: 0, limit: NUMBER_OF_POSTS_TO_FETCH },
    {
      tags: { slug: { eq: category } },
    },
  );

  if (!isLocaleInStaticRoute(locale, StaticRoutes.blog)) {
    return {
      notFound: true,
    };
  }

  const error = resourceErrorHandler(categories, undefined);

  if (error) {
    return error.notFound;
  }

  const genericProps = await strapiService.getGenericProps(locale, {
    hasKeyResources: false,
    hasLatestCaseStudy: true,
  });

  return {
    props: {
      categories: categories || [],
      description: currentCategory?.description || "",
      slug: category || "",
      slugName: currentCategory?.name,
      initialPosts: blogPosts,
      messages: await getPageTranslations(locale),
      ...genericProps,
    },
    revalidate: 300,
  };
};
